import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  Focus,
  Forward,
  Hospital,
  Info,
  MoreHorizontal,
  PenLine,
  Send,
  Stethoscope,
  Video,
} from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/Chip';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import {
  PatientOverviewPanel,
  PATIENT_INFO_PANEL_ID,
} from '@/components/patient/PatientOverviewPanel';
import { formatRevealedCpr, MASKED_CPR } from '@/lib/cprDisplay';
import { readTelephoneSubscribers } from '@/lib/patientStamkortFields';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import type { PatientRow } from '@/hooks/usePatients';

function telHref(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith('+')) return `tel:${trimmed.replace(/[^\d+]/g, '')}`;
  return `tel:${trimmed.replace(/[^\d]/g, '')}`;
}

export function PatientHeader({
  patient,
  ageYears,
  practitionerName,
  canEdit,
  canRefer,
  canWriteCorrespondence,
  canRevealCpr,
  revealedCpr,
  cprLoading,
  infoOpen,
  onEdit,
  onCorrespondence,
  onHospitalReferral,
  onRadiologyReferral,
  onSpecialistReferral,
  onRevealCpr,
  onInfoOpenChange,
  onStartVideoMeeting,
  startVideoMeetingPending,
  moreActions,
}: {
  patient: PatientRow & { has_cpr: boolean };
  ageYears?: number | null;
  practitionerName?: string | null;
  canEdit: boolean;
  canRefer: boolean;
  canWriteCorrespondence: boolean;
  canWriteGgop: boolean;
  canRevealCpr: boolean;
  revealedCpr: string | null;
  cprLoading: boolean;
  infoOpen: boolean;
  onEdit: () => void;
  onCorrespondence: () => void;
  onHospitalReferral: () => void;
  onRadiologyReferral: () => void;
  onSpecialistReferral: () => void;
  onGgop: () => void;
  onRevealCpr: () => void;
  onInfoOpenChange: (open: boolean) => void;
  onStartVideoMeeting?: () => void;
  startVideoMeetingPending?: boolean;
  moreActions?: { label: string; onClick: () => void; danger?: boolean }[];
}) {
  const { t } = useTranslation();
  const fullName = `${patient.first_name} ${patient.last_name}`.trim();
  const primaryTelephone = readTelephoneSubscribers(patient.telephone_subscribers)[0] ?? null;
  const telephone = primaryTelephone?.identifier ?? null;
  const email = patient.email?.trim() || null;
  const localeIsEn = (patient.preferred_locale as string | undefined) === 'en-DK';
  const localeLabel = localeIsEn
    ? t('patients.header.language_en')
    : t('patients.header.language_da');
  const marketingOn = patient.marketing_consent_email || patient.marketing_consent_sms;
  const cprHiddenLabel = t('patients.cpr_modal.hidden_label');
  const cprDisplay = patient.has_cpr
    ? revealedCpr
      ? formatRevealedCpr(revealedCpr)
      : MASKED_CPR
    : t('patients.cpr_currently_unset');

  const actions = (
    <>
      {canEdit ? (
        <Button type="button" variant="secondary" onClick={onEdit}>
          <PenLine aria-hidden />
          {t('patients.header.edit')}
        </Button>
      ) : null}
      {onStartVideoMeeting ? (
        <Button
          type="button"
          variant="secondary"
          onClick={onStartVideoMeeting}
          loading={startVideoMeetingPending}
          loadingText={t('patients.header.start_video_meeting')}
        >
          <Video aria-hidden />
          {t('patients.header.start_video_meeting')}
        </Button>
      ) : null}
      {canWriteCorrespondence ? (
        <Button type="button" variant="secondary" className="hidden nav:inline-flex" onClick={onCorrespondence}>
          <Send aria-hidden />
          {t('patients.correspondence.open')}
        </Button>
      ) : null}
      {canRefer ? (
        <MenuPopover
          align="end"
          className="hv-menu mt-1.5 border-transparent p-[6px] dark:border-[var(--overlay-line)]"
          trigger={
            <Button type="button" variant="secondary" className="hv-henvis px-3 nav:px-4">
              <Forward className="hidden nav:block" aria-hidden />
              {t('patients.header.refer')}
              <ChevronDown aria-hidden />
            </Button>
          }
        >
          <div className="px-2.5 pb-1 pt-2 text-[12px] font-semibold leading-4 text-ds-muted">
            {t('patients.hospital_referral.new_menu', 'Ny henvisning')}
          </div>
          <MenuItem
            icon={<Hospital className="h-5 w-5" aria-hidden />}
            onClick={onHospitalReferral}
            description={t('patients.hospital_referral.open_hint', 'Sygehushenvisning')}
          >
            {t('patients.hospital_referral.open')}
          </MenuItem>
          <MenuItem
            icon={<Focus className="h-5 w-5" aria-hidden />}
            onClick={onRadiologyReferral}
            description={t('patients.radiology_referral.open_hint', 'Billeddiagnostisk henvisning')}
          >
            {t('patients.radiology_referral.open')}
          </MenuItem>
          <MenuItem
            icon={<Stethoscope className="h-5 w-5" aria-hidden />}
            onClick={onSpecialistReferral}
            description={t('patients.specialist_referral.open_hint', 'Speciallægehenvisning')}
          >
            {t('patients.specialist_referral.open')}
          </MenuItem>
          <p className="hv-menu-foot">
            <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {t(
              'patients.hospital_referral.form_opens_in_tab',
              'Formularen åbner i fanen Henvisning.',
            )}
          </p>
        </MenuPopover>
      ) : null}
      {moreActions && moreActions.length > 0 ? (
        <MenuPopover
          align="end"
          trigger={
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label={t('patients.header.more_actions')}
            >
              <MoreHorizontal />
            </Button>
          }
        >
          {moreActions.map((action) => (
            <MenuItem key={action.label} onClick={action.onClick} danger={action.danger}>
              {action.label}
            </MenuItem>
          ))}
        </MenuPopover>
      ) : null}
    </>
  );

  return (
    <header className="pth min-w-0">
      <div className="pth-grid">
        <Avatar name={fullName} size={72} className="pth-av pth-av-lg" />
        <Avatar name={fullName} size={56} className="pth-av pth-av-sm" />

        <div className="pth-id">
          <div className="pth-name">
            <h1 className={cn(TYPE.display, 'min-w-0 text-ds-text')}>{fullName}</h1>
            {patient.status === 'archived' ? (
              <StatusChip tone="draft">{t('patients.status_archived')}</StatusChip>
            ) : null}
            {patient.status === 'related' ? (
              <StatusChip tone="neutral">{t('patients.relations.status_related')}</StatusChip>
            ) : null}
          </div>
          <div className={cn(TYPE.control, 'pth-meta text-ds-muted')}>
            <span className="inline-flex min-w-0 flex-wrap items-center gap-x-s2">
              {ageYears != null ? (
                <span className="tabular-nums">{t('patients.header.age_years', { count: ageYears })}</span>
              ) : null}
              {ageYears != null ? <FactSep /> : null}
              <span className="inline-flex min-w-0 flex-wrap items-center gap-s2">
                <span>{t('patients.section_cpr')}</span>
                <span
                  className="tracking-[0.06em] tabular-nums text-ds-text"
                  aria-label={patient.has_cpr && !revealedCpr ? cprHiddenLabel : undefined}
                >
                  {cprDisplay}
                </span>
                {patient.has_cpr && canRevealCpr ? (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto min-h-10 px-0"
                    onClick={onRevealCpr}
                    disabled={cprLoading}
                  >
                    {revealedCpr ? t('patients.cpr_hide') : t('patients.cpr_reveal')}
                  </Button>
                ) : null}
              </span>
            </span>

            {telephone ? (
              <>
                <FactSep className="hidden nav:inline" />
                <a
                  href={telHref(telephone)}
                  className="min-w-0 text-ds-text [overflow-wrap:anywhere] tabular-nums"
                >
                  {telephone}
                </a>
              </>
            ) : null}

            {email ? (
              <>
                <FactSep className="hidden nav:inline" />
                <a href={`mailto:${email}`} className="min-w-0 text-ds-text [overflow-wrap:anywhere]">
                  {email}
                </a>
              </>
            ) : null}

            <FactSep className="hidden nav:inline" />
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              className="self-start nav:self-center"
              aria-expanded={infoOpen}
              aria-controls={PATIENT_INFO_PANEL_ID}
              onClick={() => onInfoOpenChange(!infoOpen)}
            >
              {t('patients.overview_title')}
              <ChevronDown
                className={cn(
                  'h-4 w-4 motion-reduce:transition-none',
                  'transition-transform duration-200',
                  infoOpen && 'rotate-180',
                )}
                aria-hidden
              />
            </Button>
          </div>
        </div>

        <div className="pth-chips">
          <StatusChip tone="neutral">
            {t('patients.header.chip_language', { locale: localeLabel })}
          </StatusChip>
          {patient.portal_user_id ? (
            <StatusChip tone="finalized">{t('patients.header.chip_portal_active')}</StatusChip>
          ) : (
            <StatusChip tone="neutral">{t('patients.header.no_portal')}</StatusChip>
          )}
          <StatusChip tone={marketingOn ? 'upcoming' : 'neutral'}>
            {t('patients.header.chip_marketing', {
              value: marketingOn ? t('patients.header.consent_yes') : t('patients.header.consent_no'),
            })}
          </StatusChip>
          {practitionerName ? (
            <StatusChip tone="upcoming">
              {t('patients.header.chip_practitioner', { name: practitionerName })}
            </StatusChip>
          ) : null}
        </div>

        <div className="pth-actions">
          {actions}
        </div>
      </div>

      {infoOpen ? (
        <PatientOverviewPanel
          patient={patient}
          canEdit={canEdit}
          canRevealCpr={canRevealCpr}
          revealedCpr={revealedCpr}
          onRevealCpr={onRevealCpr}
          cprLoading={cprLoading}
        />
      ) : null}
    </header>
  );
}

function FactSep({ className }: { className?: string }) {
  return (
    <span className={cn('text-ds-muted', className)} aria-hidden>
      ·
    </span>
  );
}
